import { memo, useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'
import { ChevronDown, ChevronRight, Eye, EyeOff, Palette, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { KeyframeToggle } from '@/features/effects/deps/keyframes-contract'
import type { GpuEffectDefinition } from '@/infrastructure/gpu/effects'
import { cn } from '@/shared/ui/cn'
import { PropertyRow, SliderInput } from '@/shared/ui/property-controls'
import type { ItemEffect, GpuEffect } from '@/types/effects'
import type { AnimatableProperty } from '@/types/keyframe'
import { translateEffectUiText } from './effect-ui-i18n'

interface GpuWheelsPanelProps {
  itemIds: string[]
  effect: ItemEffect
  gpuEffect: GpuEffect
  definition: GpuEffectDefinition
  collapsible?: boolean
  onEditInColor?: () => void
  layout?: 'sidebar' | 'dock'
  getKeyframeProperty: (effectId: string, paramKey: string) => AnimatableProperty | null
  onParamChange: (effectId: string, paramKey: string, value: number | boolean | string) => void
  onParamLiveChange: (effectId: string, paramKey: string, value: number | boolean | string) => void
  onParamsBatchChange: (
    effectId: string,
    updates: Record<string, number | boolean | string>,
  ) => void
  onParamsBatchLiveChange: (
    effectId: string,
    updates: Record<string, number | boolean | string>,
  ) => void
  onReset: (effectId: string) => void
  onToggle: (effectId: string) => void
  onRemove: (effectId: string) => void
}

interface WheelControlProps {
  label: string
  hue: number
  amount: number
  size: number
  disabled: boolean
  dock?: boolean
  onLiveChange: (hue: number, amount: number) => void
  onCommit: (hue: number, amount: number) => void
  onReset: () => void
}

const MAX_WHEEL_SIZE = 100
const MAX_DOCK_WHEEL_SIZE = 200
const MIN_WHEEL_SIZE = 64
const MIN_DOCK_WHEEL_SIZE = 48
const GRID_GAP_PX = 4
const DOCK_GAP_PX = 28
// Vertical space each dock wheel column needs besides the wheel itself:
// label row (h-4.5 ≈ 18) + flex gaps (2 × 8) + value chip (text-[9px] ≈ 14)
// + small buffer. Used to cap the wheel diameter by the available height so
// the column never spills past the bottom of its slot when the dock pane is
// short.
const DOCK_WHEEL_EXTRAS_PX = 56
const PUCK_RADIUS_PX = 4

const WHEEL_DESCRIPTORS = [
  { label: '阴影', hueKey: 'shadowsHue', amountKey: 'shadowsAmount' },
  { label: '中间调', hueKey: 'midtonesHue', amountKey: 'midtonesAmount' },
  { label: '高光', hueKey: 'highlightsHue', amountKey: 'highlightsAmount' },
  { label: '偏移', hueKey: 'offsetHue', amountKey: 'offsetAmount' },
] as const

const TONAL_PARAMS = ['temperature', 'tint', 'saturation'] as const

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function getHueAmountFromClient(clientX: number, clientY: number, element: HTMLButtonElement) {
  const rect = element.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const dx = clientX - cx
  const dy = clientY - cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  const trackRadius = Math.max(1, rect.width / 2 - PUCK_RADIUS_PX - 1)
  const amount = clamp(dist / trackRadius, 0, 1)
  const hue = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360
  return { hue, amount }
}

const WheelControl = memo(function WheelControl({
  label,
  hue,
  amount,
  size,
  disabled,
  dock = false,
  onLiveChange,
  onCommit,
  onReset,
}: WheelControlProps) {
  const wheelRef = useRef<HTMLButtonElement>(null)
  const [dragging, setDragging] = useState(false)
  const [localHue, setLocalHue] = useState(hue)
  const [localAmount, setLocalAmount] = useState(clamp(amount, 0, 1))

  useEffect(() => {
    if (!dragging) {
      setLocalHue(hue)
      setLocalAmount(clamp(amount, 0, 1))
    }
  }, [amount, dragging, hue])

  const updateFromPointer = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const el = wheelRef.current
      if (!el) return null
      const next = getHueAmountFromClient(event.clientX, event.clientY, el)
      setLocalHue(next.hue)
      setLocalAmount(next.amount)
      onLiveChange(next.hue, next.amount)
      return next
    },
    [onLiveChange],
  )

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return
      const el = wheelRef.current
      if (!el) return
      el.setPointerCapture(event.pointerId)
      setDragging(true)
      updateFromPointer(event)
    },
    [disabled, updateFromPointer],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled || !dragging) return
      updateFromPointer(event)
    },
    [disabled, dragging, updateFromPointer],
  )

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled || !dragging) return
      const next = updateFromPointer(event)
      if (next) {
        onCommit(next.hue, next.amount)
      } else {
        onCommit(localHue, localAmount)
      }
      setDragging(false)
    },
    [disabled, dragging, localAmount, localHue, onCommit, updateFromPointer],
  )

  const handlePointerCancel = useCallback(() => {
    if (!dragging) return
    onCommit(localHue, localAmount)
    setDragging(false)
  }, [dragging, localAmount, localHue, onCommit])

  const displayTrackRadius = size / 2 - PUCK_RADIUS_PX - 1
  const puckX = Math.cos((localHue * Math.PI) / 180) * (displayTrackRadius * localAmount)
  const puckY = Math.sin((localHue * Math.PI) / 180) * (displayTrackRadius * localAmount)

  return (
    <div className={cn('flex min-w-0 flex-col items-center', dock ? 'gap-2' : 'gap-1')}>
      <div className="flex items-center gap-1">
        <span
          className={cn(
            dock ? 'text-[11px] font-medium text-foreground' : 'text-[10px] text-muted-foreground',
          )}
        >
          {label}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-4.5 w-4.5"
          onClick={onReset}
          disabled={disabled}
          title={`${translateEffectUiText('Reset to defaults')} ${label}`}
        >
          <RotateCcw className="h-3 w-3" />
        </Button>
      </div>

      <button
        ref={wheelRef}
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        className={cn(
          'relative rounded-full border border-border/70',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair',
          dock && 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)]',
        )}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          touchAction: 'none',
          backgroundImage: dock
            ? 'radial-gradient(circle closest-side, rgba(18,18,20,0.95) 0%, rgba(18,18,20,0.9) 58%, rgba(18,18,20,0.72) 78%, transparent 90%), conic-gradient(from 90deg, #ff3b30, #ff9500, #ffcc00, #34c759, #00c7be, #007aff, #5856d6, #ff2d55, #ff3b30)'
            : 'radial-gradient(circle at center, hsl(0 0% 18%) 0%, hsl(0 0% 10%) 26%, transparent 28%), conic-gradient(from 90deg, #ff3b30, #ff9500, #ffcc00, #34c759, #00c7be, #007aff, #5856d6, #ff2d55, #ff3b30)',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {dock ? (
          <>
            {/* Resolve-style crosshair ticks marking the wheel's neutral center. */}
            <span
              className="pointer-events-none absolute left-1/2 top-[3%] h-[94%] w-px -translate-x-1/2 bg-white/15"
              aria-hidden="true"
            />
            <span
              className="pointer-events-none absolute left-[3%] top-1/2 h-px w-[94%] -translate-y-1/2 bg-white/15"
              aria-hidden="true"
            />
          </>
        ) : null}
        <div
          className="absolute rounded-full border border-black/60 shadow-sm"
          style={{
            width: `${PUCK_RADIUS_PX * 2}px`,
            height: `${PUCK_RADIUS_PX * 2}px`,
            background: '#f8fafc',
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) translate(${puckX}px, ${puckY}px)`,
          }}
        />
      </button>

      <div
        className={cn(
          'font-mono tabular-nums text-muted-foreground',
          dock ? 'text-[9px]' : 'text-[10px]',
        )}
      >
        {Math.round(localHue)}° / {Math.round(localAmount * 100)}%
      </div>
    </div>
  )
})

export const GpuWheelsPanel = memo(function GpuWheelsPanel({
  itemIds,
  effect,
  gpuEffect,
  definition,
  collapsible = false,
  onEditInColor,
  layout = 'sidebar',
  getKeyframeProperty,
  onParamChange,
  onParamLiveChange,
  onParamsBatchChange,
  onParamsBatchLiveChange,
  onReset,
  onToggle,
  onRemove,
}: GpuWheelsPanelProps) {
  const isDock = layout === 'dock'
  const [collapsed, setCollapsed] = useState(collapsible ? true : false)
  const wheelGridRef = useRef<HTMLDivElement>(null)
  const [wheelSize, setWheelSize] = useState(isDock ? 128 : MAX_WHEEL_SIZE)

  const paramEntries = Object.entries(definition.params)
  const isDefault = paramEntries.every(([key, param]) => gpuEffect.params[key] === param.default)
  const showBody = !(collapsible && collapsed)

  useEffect(() => {
    const el = wheelGridRef.current
    if (!el) return

    const updateSize = () => {
      const styles = getComputedStyle(el)
      const paddingX =
        (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0)
      const paddingY =
        (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0)
      const width = el.clientWidth - paddingX
      const gap = isDock ? DOCK_GAP_PX : GRID_GAP_PX
      const maxSize = isDock ? MAX_DOCK_WHEEL_SIZE : MAX_WHEEL_SIZE
      const minSize = isDock ? MIN_DOCK_WHEEL_SIZE : MIN_WHEEL_SIZE
      const wheelCount = WHEEL_DESCRIPTORS.length
      const slotWidth = (width - gap * (wheelCount - 1)) / wheelCount
      // In the dock the wheel column also stacks a label row and the value
      // chip — cap the wheel diameter by the available height so the column
      // never spills past the bottom of its slot when the dock pane is short.
      const slotHeight = isDock
        ? el.clientHeight - paddingY - DOCK_WHEEL_EXTRAS_PX
        : Number.POSITIVE_INFINITY
      setWheelSize(clamp(Math.floor(Math.min(slotWidth, slotHeight)), minSize, maxSize))
    }

    updateSize()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => updateSize())
    observer.observe(el)
    return () => observer.disconnect()
  }, [isDock])

  const tonalRowClass = '[&>span]:w-[84px] [&>span]:min-w-[84px]'

  const renderToneControl = (key: (typeof TONAL_PARAMS)[number], compact = false) => {
    const param = definition.params[key]
    if (!param) return null
    const value = (gpuEffect.params[key] as number) ?? param.default
    const keyframeProperty = getKeyframeProperty(effect.id, key)

    if (compact) {
      return (
        <label
          key={key}
          className="grid min-w-0 grid-cols-[auto_minmax(64px,84px)] items-center gap-2"
        >
          <span className="truncate text-right text-[11px] text-muted-foreground">
            {translateEffectUiText(param.label)}
          </span>
          <div className="min-w-0">
            <SliderInput
              value={value}
              onChange={(v) => onParamChange(effect.id, key, v)}
              onLiveChange={(v) => onParamLiveChange(effect.id, key, v)}
              min={param.min ?? -100}
              max={param.max ?? 100}
              step={param.step ?? 1}
              disabled={!effect.enabled}
              className="min-w-0"
            />
          </div>
        </label>
      )
    }

    return (
      <PropertyRow key={key} label={translateEffectUiText(param.label)} className={tonalRowClass}>
        <div className="flex min-w-0 w-full items-center gap-1">
          <SliderInput
            value={value}
            onChange={(v) => onParamChange(effect.id, key, v)}
            onLiveChange={(v) => onParamLiveChange(effect.id, key, v)}
            min={param.min ?? -100}
            max={param.max ?? 100}
            step={param.step ?? 1}
            disabled={!effect.enabled}
            className="min-w-0 flex-1"
          />
          {keyframeProperty ? (
            <KeyframeToggle
              itemIds={itemIds}
              property={keyframeProperty}
              currentValue={value}
              disabled={!effect.enabled}
            />
          ) : null}
        </div>
      </PropertyRow>
    )
  }

  return (
    <div className={cn('space-y-0', isDock && 'flex h-full min-h-0 flex-col overflow-hidden')}>
      <PropertyRow label={translateEffectUiText(definition.name)}>
        <div className="flex min-w-0 w-full items-center justify-end gap-1">
          {onEditInColor ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={onEditInColor}
              title="进入调色工作区"
              aria-label="进入调色工作区"
            >
              <Palette className="h-3 w-3" />
            </Button>
          ) : null}
          {collapsible ? (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              className="flex flex-1 items-center gap-1 min-w-0 text-left text-xs text-muted-foreground hover:text-foreground"
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="truncate">{translateEffectUiText(definition.name)}</span>
              {!isDefault ? (
                <span
                  className="ml-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-6 w-6 flex-shrink-0', isDefault && 'opacity-30')}
            onClick={() => onReset(effect.id)}
            title={translateEffectUiText('Reset to defaults')}
            disabled={isDefault}
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onToggle(effect.id)}
            title={translateEffectUiText(effect.enabled ? 'Disable effect' : 'Enable effect')}
          >
            {effect.enabled ? (
              <Eye className="h-3 w-3" />
            ) : (
              <EyeOff className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onRemove(effect.id)}
            title={translateEffectUiText('Remove effect')}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </PropertyRow>

      {showBody ? (
        isDock ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="grid shrink-0 grid-cols-3 gap-x-3 border-b border-border/70 px-4 py-1.5">
              {TONAL_PARAMS.map((key) => renderToneControl(key, true))}
            </div>
            <div ref={wheelGridRef} className="min-h-0 flex-1 overflow-hidden px-6 py-3">
              <div className="grid min-h-full grid-cols-[repeat(4,minmax(3rem,1fr))] items-center gap-7">
                {WHEEL_DESCRIPTORS.map((desc) => (
                  <WheelControl
                    key={desc.label}
                    label={desc.label}
                    hue={(gpuEffect.params[desc.hueKey] as number) ?? 0}
                    amount={(gpuEffect.params[desc.amountKey] as number) ?? 0}
                    size={wheelSize}
                    disabled={!effect.enabled}
                    dock
                    onLiveChange={(hue, amount) => {
                      onParamsBatchLiveChange(effect.id, {
                        [desc.hueKey]: hue,
                        [desc.amountKey]: amount,
                      })
                    }}
                    onCommit={(hue, amount) => {
                      onParamsBatchChange(effect.id, {
                        [desc.hueKey]: hue,
                        [desc.amountKey]: amount,
                      })
                    }}
                    onReset={() => {
                      onParamsBatchChange(effect.id, {
                        [desc.hueKey]: 0,
                        [desc.amountKey]: 0,
                      })
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="px-2 pb-2">
              <div ref={wheelGridRef} className="grid grid-cols-4 gap-1">
                {WHEEL_DESCRIPTORS.map((desc) => (
                  <WheelControl
                    key={desc.label}
                    label={desc.label}
                    hue={(gpuEffect.params[desc.hueKey] as number) ?? 0}
                    amount={(gpuEffect.params[desc.amountKey] as number) ?? 0}
                    size={wheelSize}
                    disabled={!effect.enabled}
                    onLiveChange={(hue, amount) => {
                      onParamsBatchLiveChange(effect.id, {
                        [desc.hueKey]: hue,
                        [desc.amountKey]: amount,
                      })
                    }}
                    onCommit={(hue, amount) => {
                      onParamsBatchChange(effect.id, {
                        [desc.hueKey]: hue,
                        [desc.amountKey]: amount,
                      })
                    }}
                    onReset={() => {
                      onParamsBatchChange(effect.id, {
                        [desc.hueKey]: 0,
                        [desc.amountKey]: 0,
                      })
                    }}
                  />
                ))}
              </div>
            </div>
            {TONAL_PARAMS.map((key) => renderToneControl(key))}
          </>
        )
      ) : null}
    </div>
  )
})
